"use client";

import { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";

import { t } from "@/lib/localization";
import { HtmlBlock as HtmlBlockType } from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";

/**
 * This block exists to paste embed codes (Genially, YouTube, H5P, …), but
 * DOMPurify drops <iframe> by default, which silently rendered an empty box.
 * Keep iframes + their embed attributes; javascript:/data: srcs are still
 * blocked by DOMPurify's URI check.
 */
const SANITIZE_OPTS = {
    ADD_TAGS: ["iframe"],
    ADD_ATTR: [
        "allow",
        "allowfullscreen",
        "frameborder",
        "scrolling",
        "loading",
        "referrerpolicy",
    ],
};

/**
 * A pasted *full HTML document* (an interactive game/app with its own
 * <script>/<style>) must RUN, not be sanitized to text — DOMPurify strips
 * <script>, so inline JS never executes. Detect that case and render it in a
 * sandboxed iframe (`srcDoc`) so its scripts/styles run, fully isolated from the
 * app: no `allow-same-origin`, so the content gets a unique opaque origin and
 * can't touch the parent's DOM/cookies/storage. Embed snippets (Genially/YouTube
 * iframes) contain no such tags → they keep the sanitized inline path.
 */
const RUNNABLE = /<!doctype\s|<html[\s>]|<script[\s>]|<style[\s>]/i;
const isRunnable = (html: string) => RUNNABLE.test(html);

function RunnableHtml({ html }: { html: string }) {
    return (
        <iframe
            title="html"
            srcDoc={html}
            sandbox="allow-scripts allow-popups allow-forms allow-modals allow-presentation"
            className="h-[600px] w-full rounded-lg border bg-white"
        />
    );
}

function useSanitized(html: string) {
    const [out, setOut] = useState("");
    useEffect(() => {
        setOut(DOMPurify.sanitize(html, SANITIZE_OPTS));
    }, [html]);
    return out;
}

export default function HtmlBlock({ block }: { block: HtmlBlockType }) {
    const updateBlock = useLomdaV3Editor((s) => s.updateBlock);

    const editable = useBlockEditable();
    const [draft, setDraft] = useState(block.data.html);
    // Transient view state, owned by the store so the topbar can drive it.
    const preview = useLomdaV3Editor((s) => s.htmlPreview[block.id] ?? false);
    const safeReadOnly = useSanitized(block.data.html);
    const safePreview = useSanitized(draft);

    // Flush the draft when the editor closes. It previously only committed on
    // the Preview button or a textarea blur, so closing the editor without
    // blurring silently dropped the edit.
    const draftRef = useRef(draft);
    draftRef.current = draft;
    const savedRef = useRef(block.data.html);
    savedRef.current = block.data.html;
    const blockIdRef = useRef(block.id);
    blockIdRef.current = block.id;

    // Commit to the store shortly after each change (not only on blur/unmount),
    // so switching page — which may not blur the textarea — can't drop the edit.
    const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onDraftChange = (value: string) => {
        setDraft(value);
        if (commitTimer.current) clearTimeout(commitTimer.current);
        commitTimer.current = setTimeout(() => {
            updateBlock(blockIdRef.current, { html: value });
        }, 300);
    };

    useEffect(
        () => () => {
            if (commitTimer.current) clearTimeout(commitTimer.current);
            if (draftRef.current !== savedRef.current) {
                updateBlock(blockIdRef.current, { html: draftRef.current });
            }
        },
        [updateBlock]
    );

    if (!editable) {
        return isRunnable(block.data.html) ? (
            <RunnableHtml html={block.data.html} />
        ) : (
            <div
                className="lomda-v3-rich-text"
                dangerouslySetInnerHTML={{ __html: safeReadOnly }}
            />
        );
    }

    // Code/Preview lives on the second-level topbar (htmlSettings) — the block
    // just renders whichever the topbar selected.
    if (preview) {
        return isRunnable(draft) ? (
            <RunnableHtml html={draft} />
        ) : (
            <div
                className="lomda-v3-rich-text rounded-lg border p-4"
                dangerouslySetInnerHTML={{ __html: safePreview }}
            />
        );
    }

    return (
        <textarea
            value={draft}
            onChange={(e) => onDraftChange(e.target.value)}
            onBlur={() => updateBlock(block.id, { html: draft })}
            placeholder={t("lomda-v3-editor.html.placeholder")}
            dir="ltr"
            spellCheck={false}
            className="min-h-40 w-full rounded-lg border bg-muted/30 p-3 font-mono lomda-v3-text-small outline-none"
        />
    );
}
